// =============================================================================
// MARGE EVOLUTION CHART - Courbes fluides style premium
// =============================================================================

import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';
import { EvolutionDataPoint } from '@/hooks/useChartData';
import {
  CHART_SERIES_COLORS,
  CHART_GRADIENTS,
  formatMontantChart,
  TOOLTIP_EXPLANATIONS,
  tooltipContainerStyle,
} from './chartConfig';

interface Props {
  data: EvolutionDataPoint[];
}

// =============================================================================
// TOOLTIP PERSONNALISÉ PREMIUM
// =============================================================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const margeData = payload.find((p: any) => p.dataKey === 'marge');
  const coutsData = payload.find((p: any) => p.dataKey === 'coutsCumules');
  const budgetData = payload.find((p: any) => p.dataKey === 'budget');

  return (
    <div style={tooltipContainerStyle} className="min-w-[280px]">
      {/* En-tête avec date */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <span className="text-sm font-medium text-white/90">{label}</span>
      </div>

      {/* Marge */}
      {margeData && (
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
            style={{ backgroundColor: CHART_SERIES_COLORS.serie4 }}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Marge à date</span>
              <span className="text-lg font-bold text-white">
                {formatMontantChart(margeData.value)}
              </span>
            </div>
            <p className="text-xs text-white/50 mt-1">
              {TOOLTIP_EXPLANATIONS.marge}
            </p>
          </div>
        </div>
      )}

      {/* Coûts cumulés */}
      {coutsData && (
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
            style={{ backgroundColor: CHART_SERIES_COLORS.serie1 }}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Coûts cumulés</span>
              <span className="text-lg font-bold text-white">
                {formatMontantChart(coutsData.value)}
              </span>
            </div>
            <p className="text-xs text-white/50 mt-1">
              {TOOLTIP_EXPLANATIONS.coutsCumules}
            </p>
          </div>
        </div>
      )}

      {/* Budget */}
      {budgetData && (
        <div className="flex items-start gap-3">
          <div
            className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
            style={{ backgroundColor: CHART_SERIES_COLORS.serie2 }}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Budget total</span>
              <span className="text-lg font-bold text-white">
                {formatMontantChart(budgetData.value)}
              </span>
            </div>
            <p className="text-xs text-white/50 mt-1">
              {TOOLTIP_EXPLANATIONS.budget}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LÉGENDE PERSONNALISÉE
// =============================================================================

function CustomLegend() {
  const items = [
    { name: 'Marge à date', color: CHART_SERIES_COLORS.serie4 },
    { name: 'Coûts cumulés', color: CHART_SERIES_COLORS.serie1 },
    { name: 'Budget', color: CHART_SERIES_COLORS.serie2 },
  ];

  return (
    <div className="flex flex-wrap gap-6 mt-4 justify-center">
      {items.map((item) => (
        <div key={item.name} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-sm text-muted-foreground">{item.name}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function MargeEvolutionChart({ data }: Props) {
  // Calcul du domaine Y pour un affichage optimal
  const yDomain = useMemo(() => {
    if (!data.length) return [0, 100000];

    const allValues = data.flatMap((d) => [d.marge, d.coutsCumules, d.budget]);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 10000;

    return [Math.floor((min - padding) / 1000) * 1000, Math.ceil((max + padding) / 1000) * 1000];
  }, [data]);

  // Empty state : pas de données OU un seul point (pas assez pour tracer une courbe)
  if (data.length < 2) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <Info className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-sm font-medium">Pas de données d'évolution</p>
        <p className="text-xs mt-2 opacity-70 text-center max-w-xs">
          Les courbes apparaîtront après le premier enregistrement quotidien du chantier.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
        >
          {/* Définitions des dégradés */}
          <defs>
            <linearGradient id="gradientMarge" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie4} stopOpacity={0.4} />
              <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie4} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradientCouts" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie1} stopOpacity={0.4} />
              <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie1} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradientBudget" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie2} stopOpacity={0.2} />
              <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie2} stopOpacity={0} />
            </linearGradient>

            {/* Filtre glow pour les courbes */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grille subtile */}
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="hsl(0 0% 25%)"
            strokeOpacity={0.3}
            vertical={false}
          />

          {/* Axes */}
          <XAxis
            dataKey="dateLabel"
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'hsl(0 0% 60%)', fontSize: 12 }}
            dy={10}
          />
          <YAxis
            domain={yDomain}
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'hsl(0 0% 60%)', fontSize: 12 }}
            tickFormatter={(value) => formatMontantChart(value)}
            width={70}
          />

          {/* Tooltip */}
          <Tooltip content={<CustomTooltip />} />

          {/* Aires et courbes */}
          <Area
            type="monotone"
            dataKey="budget"
            stroke={CHART_SERIES_COLORS.serie2}
            strokeWidth={2}
            strokeDasharray="5 5"
            fill="url(#gradientBudget)"
            dot={false}
            animationDuration={1500}
            animationEasing="ease-out"
          />

          <Area
            type="monotone"
            dataKey="coutsCumules"
            stroke={CHART_SERIES_COLORS.serie1}
            strokeWidth={3}
            fill="url(#gradientCouts)"
            dot={false}
            filter="url(#glow)"
            animationDuration={1500}
            animationEasing="ease-out"
          />

          <Area
            type="monotone"
            dataKey="marge"
            stroke={CHART_SERIES_COLORS.serie4}
            strokeWidth={3}
            fill="url(#gradientMarge)"
            dot={false}
            filter="url(#glow)"
            animationDuration={1500}
            animationEasing="ease-out"
          />
        </AreaChart>
      </ResponsiveContainer>

      <CustomLegend />
    </div>
  );
}
